import _ from 'lodash';
import GoogleMapReact from 'google-map-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AutoComplete, Button, Col, Form, Input, notification, Row,
} from 'antd';
import { Marker } from '../../../components';
import { getOrganizations } from '../../../services';
import { setActiveProject } from '../../../store/common';
import { createMasterProject, updateMasterProject } from '../../../services/projects-new';
import SearchAddress from './SearchAddress';

function BasicInfo({
  onFinish,
  basicInfo = {},
  projectId = null,
  setLoader = () => {},
  history,
}) {
  const dispatch = useDispatch();
  const { user } = useSelector(({ auth }) => auth);
  const [form] = Form.useForm();
  const [apiReady, setApiReady] = useState(false);
  const [map, setMap] = useState(null);
  const [mapApi, setMapApi] = useState(null);
  const [address, setAddress] = useState(null);
  const [center, setCenter] = useState([]);
  const [lat, setLat] = useState();
  const [lng, setLng] = useState();
  const [organizations, setOrganizations] = useState([]);

  useEffect(() => {
    fetchOrgs();
  }, []);

  const setCurrentLocation = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        setCenter([position.coords.latitude, position.coords.longitude]);
        setLat(position.coords.latitude);
        setLng(position.coords.longitude);
      });
    }
  };

  const fetchOrgs = async (val) => {
    try {
      const results = await getOrganizations(val);
      const filteredMfrs = results.data.filter((item) => item);
      setOrganizations([...filteredMfrs]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    form.setFieldsValue({ ...basicInfo });
    if (basicInfo?.latitude && basicInfo?.longitude) {
      setCenter([basicInfo.latitude, basicInfo.longitude]);
      setLat(basicInfo.latitude);
      setLng(basicInfo.longitude);
    } else {
      setCurrentLocation();
    }
  }, [basicInfo]);

  const handleApiLoaded = (map, maps) => {
    if (map && maps) {
      setApiReady(true);
      setMap(map);
      setMapApi(maps);
    }
  };

  const onSubmit = async (values) => {
    setLoader(true);
    delete basicInfo.tags;
    delete basicInfo.users;
    values.latitude = lat;
    values.longitude = lng;
    values.branch = user?.branch || null;
    let response;

    if (!_.isEqual(basicInfo, values)) {
      if (projectId) {
        response = await updateMasterProject(projectId, values);
        notification.success({ description: response.message || 'Info updated' });
      } else {
        response = await createMasterProject({ ...values });
        notification.success({ description: response.message || 'Error Updating Info' });
        dispatch(
          setActiveProject({
            masterProject: response?.data,
            id: response?.data?.id,
            name: response?.data?.name,
          }),
        );
        history.push({
          pathname: `/projects/${response.data.id}`,
        });
      }
    }

    setLoader(false);
    onFinish(values);
  };

  const _generateAddress = (_lat, _lng) => {
    const geocoder = new mapApi.Geocoder();
    geocoder.geocode({ location: { lat: _lat, lng: _lng } }, (results, status) => {
      if (status === 'OK') {
        if (results[0]) {
          setAddress(results[0].formatted_address);
          form.setFieldsValue({ address: results[0].formatted_address });
        } else {
        }
      } else {
      }
    });
  };

  const _onChange = ({ center, zoom }) => {
    setCenter(center);
  };

  const _onClick = (value) => {
    const { lat, lng } = value;
    setLat(lat);
    setLng(lng);
    _generateAddress(lat, lng);
  };

  const addPlace = (place) => {
    setLat(place.geometry.location.lat());
    setLng(place.geometry.location.lng());
    setAddress(place.formatted_address);
    form.setFieldsValue({
      address: place.formatted_address,
    });
  };

  return (
    <>
      <Row className="row">
        <Col flex="8 0 auto" className="column">
          {projectId ? (
            <h2 className="step-heading mb-20">Edit Master Information</h2>
          ) : (
            <h2 className="step-heading mb-20">Master Project Information</h2>
          )}
        </Col>
      </Row>
      <Form form={form} layout="vertical" onFinish={onSubmit} initialValues={basicInfo}>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              message: 'Required',
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              required: false,
              message: 'Required',
            },
          ]}
        >
          <Input.TextArea />
        </Form.Item>
        <Form.Item
          label="Owner Organization"
          tooltip="The company to which the equipment will be owned (also known as End User)"
          name="owner"
          rules={[
            {
              required: true,
              message: 'Owner is required',
            },
          ]}
        >
          <AutoComplete
            virtual
            showSearch
            placeholder="Search or Select an organization"
            optionFilterProp="children"
            autoClearSearchValue={false}
            filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
          >
            {organizations.map((org) => (
              <AutoComplete.Option value={org.name}>{org.name}</AutoComplete.Option>
            ))}
          </AutoComplete>
        </Form.Item>
        <Form.Item
          label="Site Address"
          name="address"
          rules={[
            {
              required: false,
              message: 'Site address is required',
            },
          ]}
        >
          {apiReady && (
          <SearchAddress
            map={map}
            mapApi={mapApi}
            addplace={addPlace}
            onKeyDown={(e) => (e.keyCode === 13 ? e.preventDefault() : '')}
          />
          )}
        </Form.Item>

        <div style={{ height: '442px', width: '100%' }}>
          <GoogleMapReact
            zoom={4}
            center={center}
            bootstrapURLKeys={{
              key: process.env.REACT_APP_GOOGLE_MAP_KEY_V3,
              libraries: ['places', 'geometry'],
            }}
            onChange={_onChange}
            onClick={_onClick}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
          >
            {lat && lng && <Marker text={address} lat={lat} lng={lng} />}
          </GoogleMapReact>
        </div>
        <Form.Item className="text-right mb-0">
          <Button
            className="btn-lg btn-primary-lg save-btn mt-30"
            type="primary"
            htmlType="submit"
          >
            {projectId ? 'Update' : 'Save'}
          </Button>
        </Form.Item>
      </Form>
    </>
  );
}

export default BasicInfo;
